/* ============================================================
   JuxtaCharge theme runtime
   - Section registry: each section registers init/destroy so the
     Shopify theme editor can re-run JS when sections are added,
     reordered, or selected.
   - Global helpers: cart drawer, format money.
   ============================================================ */
(function () {
  const JuxtaTheme = {
    sections: {},

    /**
     * Register a section module.
     * name = the HTML data-section attribute (usually section.id in Liquid)
     * def = { init(el), destroy(el) }
     */
    register(name, def) {
      this.sections[name] = def;
    },

    /**
     * Initialize all sections found in the document or within a given root.
     */
    initAll(root) {
      (root || document).querySelectorAll('[data-section-type]').forEach((el) => {
        this.initSection(el);
      });
    },

    initSection(target) {
      // target may be the container itself, or contain .shopify-section-<id>
      const containers = [];
      if (target.hasAttribute && target.hasAttribute('data-section-type')) {
        containers.push(target);
      }
      target.querySelectorAll && target.querySelectorAll('[data-section-type]').forEach((el) => containers.push(el));
      containers.forEach((el) => {
        const type = el.getAttribute('data-section-type');
        const def = this.sections[type];
        if (def && typeof def.init === 'function' && !el.__juxta_initialized) {
          try { def.init(el); el.__juxta_initialized = true; } catch (e) { console.warn('[juxta] init', type, e); }
        }
      });
    },

    destroySection(target) {
      const containers = [];
      if (target.hasAttribute && target.hasAttribute('data-section-type')) {
        containers.push(target);
      }
      target.querySelectorAll && target.querySelectorAll('[data-section-type]').forEach((el) => containers.push(el));
      containers.forEach((el) => {
        const type = el.getAttribute('data-section-type');
        const def = this.sections[type];
        if (def && typeof def.destroy === 'function') {
          try { def.destroy(el); } catch (e) { console.warn('[juxta] destroy', type, e); }
        }
        el.__juxta_initialized = false;
      });
    },

    /* Cart helpers ------------------------------------------------------ */
    formatMoney(cents) {
      try {
        return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: window.Shopify && Shopify.currency && Shopify.currency.active || 'USD' });
      } catch (e) {
        return '$' + (cents / 100).toFixed(2);
      }
    },

    async fetchCart() {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }});
      return res.json();
    },

    async addToCart(formData) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      if (!res.ok) throw new Error('add to cart failed');
      return res.json();
    },

    async changeCartItem(key, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      return res.json();
    },

    updateCartCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    },

    openCart() {
      const drawer = document.getElementById('CartDrawer');
      const overlay = document.getElementById('CartDrawerOverlay');
      if (!drawer) { window.location.href = '/cart'; return; }
      this.refreshDrawer();
      drawer.classList.add('open');
      if (overlay) overlay.classList.add('open');
    },

    closeCart() {
      const drawer = document.getElementById('CartDrawer');
      const overlay = document.getElementById('CartDrawerOverlay');
      if (drawer) drawer.classList.remove('open');
      if (overlay) overlay.classList.remove('open');
    },

    getTierDiscount(quantity) {
      const tiers = (window.JuxtaCartTiers || []).slice().sort((a, b) => b.qty - a.qty);
      for (const tier of tiers) {
        if (quantity >= tier.qty && tier.pct > 0) return tier.pct;
      }
      return 0;
    },

    renderCartItem(item) {
      const qty        = item.quantity;
      // Use Shopify's real cart pricing so the drawer matches checkout exactly,
      // including automatic discounts (e.g. the bundle quantity discounts).
      const finalLine  = item.final_line_price;     // line total after all discounts (cents)
      const origLine   = (item.original_line_price != null) ? item.original_line_price : (item.original_price * qty);
      const savingsAmt = Math.max(0, origLine - finalLine);
      const showCompare = origLine > finalLine;

      // Pull the discount name from Shopify's line-level allocations, if present
      let discountTitle = '';
      const allocs = item.line_level_discount_allocations || [];
      if (allocs.length && allocs[0].discount_application) {
        discountTitle = allocs[0].discount_application.title || '';
      }

      const priceHTML = `
        <span class="cart-drawer__item-price">${this.formatMoney(finalLine)}</span>
        ${showCompare ? `<span class="cart-drawer__item-compare">${this.formatMoney(origLine)}</span>` : ''}
      `;
      const savingsHTML = savingsAmt > 0
        ? `<div class="cart-drawer__item-savings">
            <span class="cart-drawer__discount-badge">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
              ${discountTitle ? discountTitle : 'Discount'}
            </span>${this.formatMoney(savingsAmt)} off</div>`
        : '';

      const variantHTML = item.variant_title && item.variant_title !== 'Default Title'
        ? `<div class="cart-drawer__item-variant">${item.variant_title}</div>`
        : '';

      return `
        <div class="cart-drawer__item" data-key="${item.key}" data-base-price="${item.original_price}" data-discounted-line="${finalLine}">
          <img src="${item.image || ''}" alt="${item.product_title}">
          <div class="cart-drawer__item-info">
            <div class="cart-drawer__item-top">
              <div class="cart-drawer__item-title">${item.product_title}</div>
              <button type="button" class="cart-drawer__remove" data-cart-remove="${item.key}" aria-label="Remove item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </button>
            </div>
            ${variantHTML}
            <div class="cart-drawer__item-price-row">${priceHTML}</div>
            ${savingsHTML}
            <div class="cart-drawer__qty">
              <button type="button" class="cart-drawer__qty-btn" data-qty-change="${item.key}" data-qty="${qty - 1}" aria-label="Decrease quantity">−</button>
              <span class="cart-drawer__qty-num">${qty}</span>
              <button type="button" class="cart-drawer__qty-btn" data-qty-change="${item.key}" data-qty="${qty + 1}" aria-label="Increase quantity">+</button>
            </div>
          </div>
        </div>
      `;
    },

    async refreshDrawer() {
      const cart = await this.fetchCart();
      this.updateCartCount(cart.item_count);
      const itemsEl = document.getElementById('CartDrawerItems');
      const subtotalEl = document.getElementById('CartDrawerSubtotal');
      if (!itemsEl) return;
      if (cart.items.length === 0) {
        itemsEl.innerHTML = '<div class="cart-drawer__empty">Your cart is empty</div>';
      } else {
        itemsEl.innerHTML = cart.items.map((item) => this.renderCartItem(item)).join('');
      }
      // Use Shopify's real cart total (reflects automatic discounts) for the subtotal
      if (subtotalEl) subtotalEl.textContent = this.formatMoney(cart.total_price);
    }
  };

  window.JuxtaTheme = JuxtaTheme;

  /* Wire up cart drawer global events */
  document.addEventListener('click', function (e) {
    const openBtn = e.target.closest('[data-cart-open]');
    if (openBtn) { e.preventDefault(); JuxtaTheme.openCart(); return; }
    const closeBtn = e.target.closest('[data-cart-close]');
    if (closeBtn) { e.preventDefault(); JuxtaTheme.closeCart(); return; }
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      const key = removeBtn.getAttribute('data-cart-remove');
      JuxtaTheme.changeCartItem(key, 0).then(() => JuxtaTheme.refreshDrawer());
      return;
    }
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      const key = qtyBtn.getAttribute('data-qty-change');
      const qty = parseInt(qtyBtn.getAttribute('data-qty'), 10);
      JuxtaTheme.changeCartItem(key, Math.max(0, qty)).then(() => JuxtaTheme.refreshDrawer());
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    JuxtaTheme.fetchCart().then((cart) => JuxtaTheme.updateCartCount(cart.item_count)).catch(() => {});
    JuxtaTheme.initAll(document);
  });
})();
